/**
 * Phone number extraction + formatting — FEST-PHASE-2.E.
 *
 * Buyers send phone numbers in every imaginable shape:
 *   555-123-4567   555.123.4567   555 123 4567   5551234567
 *   (555) 123-4567   +1 555-123-4567   1.555.123.4567
 *
 * We normalize all matched numbers to E.164 (+1XXXXXXXXXX) for storage
 * and provide formatting / iMessage-href helpers for display.
 *
 * Constraints:
 *   - North American Numbering Plan only (US/Canada). Area code and
 *     local exchange both must start 2-9 — this also cuts most false
 *     positives like prices and dates.
 *   - Country code 1 is optional with an optional + prefix.
 *   - Separators: space, dash, period, parentheses around area code.
 *     Mixed within the same number is fine.
 */

/**
 * Strict NANP regex. Anchored loosely with word boundaries on the
 * area-code parenthesis case so we don't grab partial digit runs.
 */
const PHONE_RE = new RegExp(
  String.raw`(?<![\d])` +              // not preceded by a digit (avoids "12345551234567")
  String.raw`(?:\+?1[\s.\-]?)?` +      // optional country code
  String.raw`\(?([2-9]\d{2})\)?` +     // area code, optional parens
  String.raw`[\s.\-]?` +
  String.raw`([2-9]\d{2})` +           // exchange
  String.raw`[\s.\-]?` +
  String.raw`(\d{4})` +                // line
  String.raw`(?!\d)`,                  // not followed by another digit
  "g",
);

/** Extract all unique E.164 phone numbers from a block of text. */
export function extractPhones(text: string): string[] {
  if (!text) return [];
  const seen = new Set<string>();
  for (const m of text.matchAll(PHONE_RE)) {
    seen.add(`+1${m[1]}${m[2]}${m[3]}`);
  }
  return [...seen];
}

/** "+15551234567" → "+1 555-123-4567". Falls back to input on bad shape. */
export function formatPhone(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  if (!m) return e164;
  return `+1 ${m[1]}-${m[2]}-${m[3]}`;
}

/**
 * "+15551234567" → "imessage:+15551234567" — clicking opens Messages.app
 * on macOS and Messages on iOS with the number pre-filled.
 */
export function imessageHref(e164: string): string {
  return `imessage:${e164}`;
}

/** "+15551234567" → "tel:+15551234567" — fallback for non-Apple clients. */
export function telHref(e164: string): string {
  return `tel:${e164}`;
}

/** Permissively normalize a user-typed phone to E.164. Returns null on bad input. */
export function normalizePhone(input: string): string | null {
  const digits = input.replace(/[^\d]/g, "");
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return null;
}
